import { useCallback, useEffect, useMemo, useState } from "react";
import PortalLayout from "../../components/PortalLayout";
import { api, formatApiErrorDetail, formatDate } from "../../lib/api";

const STATUS_FILTERS = [
  { value: "submitted", label: "In coda", color: "var(--br-bronze)" },
  { value: "approved", label: "Approvati", color: "var(--br-bronze-soft)" },
  { value: "rejected", label: "Rifiutati", color: "var(--br-red)" },
  { value: "", label: "Tutti", color: "var(--br-text-dim)" },
];

const STATUS_COLOR = {
  submitted: "var(--br-bronze)",
  approved: "var(--br-bronze-soft)",
  rejected: "var(--br-red)",
  pending: "var(--br-text-dim)",
};

export default function AdminKycPage() {
  const [filter, setFilter] = useState("submitted");
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [review, setReview] = useState(null); // { row, decision }

  const load = useCallback(async () => {
    try {
      const qs = filter ? `?status=${filter}` : "";
      const { data } = await api.get(`/admin/kyc${qs}`);
      setRows(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const openFile = (id) => {
    const url = `${api.defaults.baseURL}/kyc/file/${id}`;
    window.open(url, "_blank", "noopener");
  };

  const counts = useMemo(() => {
    const c = { submitted: 0, approved: 0, rejected: 0 };
    rows.forEach((r) => {
      if (c[r.status] !== undefined) c[r.status]++;
    });
    return c;
  }, [rows]);

  return (
    <PortalLayout>
      <section
        className="max-w-[1280px] mx-auto px-6 sm:px-8 pt-14 pb-8"
        data-testid="admin-kyc-page"
      >
        <div className="br-fade-up">
          <div className="br-overline">Compliance</div>
          <h1 className="font-serif-display text-[clamp(2.4rem,4.5vw,3.6rem)] mt-3">
            Verifiche <em>KYC</em>.
          </h1>
          <p className="text-[0.95rem] text-[color:var(--br-text-dim)] mt-3 max-w-[640px]">
            Coda di documenti in attesa di revisione. Ogni decisione viene notificata via email al
            cliente (con copia al team Compliance).
          </p>
        </div>

        {/* Filter pills */}
        <div className="mt-8 flex flex-wrap items-center gap-3 br-fade-up br-delay-1" data-testid="kyc-filters">
          {STATUS_FILTERS.map((f) => {
            const active = filter === f.value;
            return (
              <button
                key={f.value || "all"}
                onClick={() => setFilter(f.value)}
                className="font-mono-fin text-[0.66rem] tracking-[0.2em] uppercase px-4 py-2 border transition-colors"
                style={{
                  borderColor: active ? f.color : "var(--br-line-bright)",
                  color: active ? f.color : "var(--br-text-dim)",
                  background: active ? "rgba(167,128,73,0.06)" : "transparent",
                }}
                data-testid={`kyc-filter-${f.value || "all"}`}
              >
                {f.label}
              </button>
            );
          })}
          <div className="ml-auto font-mono-fin text-[0.72rem] text-[color:var(--br-text-dim)]" data-testid="kyc-counts">
            in coda: {counts.submitted} · approvati: {counts.approved} · rifiutati: {counts.rejected}
          </div>
        </div>

        {error && (
          <div className="mt-6 br-card p-4 text-[color:var(--br-red)]" data-testid="admin-kyc-error">
            {String(error)}
          </div>
        )}

        <div
          className="mt-6 br-card overflow-x-auto br-fade-up br-delay-2"
          data-testid="admin-kyc-table"
        >
          <table className="w-full text-left">
            <thead>
              <tr style={{ borderBottom: "1px solid var(--br-line)" }}>
                <Th>Cliente</Th>
                <Th>Documento</Th>
                <Th>Stato</Th>
                <Th>Inviato</Th>
                <Th>Audit ID</Th>
                <Th align="right">Azioni</Th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-5 py-12 text-center text-[color:var(--br-text-dim)] italic"
                    data-testid="kyc-empty"
                  >
                    Nessun documento nel filtro corrente.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className="border-b"
                  style={{ borderColor: "var(--br-line)" }}
                  data-testid={`kyc-row-${r.id}`}
                >
                  <Td>
                    <div className="text-[color:var(--br-parchment)]">{r.user.name}</div>
                    <div className="font-mono-fin text-[0.78rem] text-[color:var(--br-text-dim)]">
                      {r.user.email}
                    </div>
                  </Td>
                  <Td>
                    <div className="text-[color:var(--br-parchment)]">{r.title_it}</div>
                    <div className="font-mono-fin text-[0.7rem] text-[color:var(--br-text-dim)]">
                      {r.doc_type}
                    </div>
                  </Td>
                  <Td>
                    <span
                      className="font-mono-fin text-[0.62rem] tracking-[0.22em] uppercase border px-2 py-1"
                      style={{
                        color: STATUS_COLOR[r.status] || "var(--br-text-dim)",
                        borderColor: STATUS_COLOR[r.status] || "var(--br-line-bright)",
                      }}
                    >
                      {r.status}
                    </span>
                  </Td>
                  <Td className="font-mono-fin text-[color:var(--br-text-dim)] text-[0.8rem]">
                    {r.submitted_at ? formatDate(r.submitted_at) : "—"}
                  </Td>
                  <Td className="font-mono-fin text-[0.72rem] text-[color:var(--br-text-dim)]">
                    {r.audit?.audit_id || "—"}
                  </Td>
                  <Td align="right">
                    <div className="flex items-center justify-end gap-2">
                      {r.has_file && (
                        <button
                          onClick={() => openFile(r.id)}
                          className="br-btn-outline"
                          style={{ padding: "6px 12px", fontSize: "0.62rem" }}
                          data-testid={`kyc-open-${r.id}`}
                        >
                          Apri
                        </button>
                      )}
                      {r.status !== "approved" && (
                        <button
                          onClick={() => setReview({ row: r, decision: "approved" })}
                          className="br-btn-outline"
                          style={{
                            padding: "6px 12px",
                            fontSize: "0.62rem",
                            borderColor: "var(--br-bronze-soft)",
                            color: "var(--br-bronze-soft)",
                          }}
                          data-testid={`kyc-approve-${r.id}`}
                        >
                          Approva
                        </button>
                      )}
                      {r.status !== "rejected" && (
                        <button
                          onClick={() => setReview({ row: r, decision: "rejected" })}
                          className="br-btn-outline"
                          style={{
                            padding: "6px 12px",
                            fontSize: "0.62rem",
                            borderColor: "var(--br-red)",
                            color: "var(--br-red)",
                          }}
                          data-testid={`kyc-reject-${r.id}`}
                        >
                          Rifiuta
                        </button>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {review && (
        <ReviewModal
          info={review}
          onCancel={() => setReview(null)}
          onDone={() => {
            setReview(null);
            load();
          }}
          onError={(msg) => setError(msg)}
        />
      )}
    </PortalLayout>
  );
}

function ReviewModal({ info, onCancel, onDone, onError }) {
  const { row, decision } = info;
  const isApprove = decision === "approved";
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!isApprove && !comment.trim()) {
      onError("Indica il motivo del rifiuto: il commento è obbligatorio.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/kyc/${row.id}/${isApprove ? "approve" : "reject"}`, {
        comment: comment.trim() || null,
      });
      onDone();
    } catch (e) {
      onError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-6 py-12 overflow-y-auto"
      style={{ background: "rgba(10,14,26,0.78)", backdropFilter: "blur(8px)" }}
      onClick={onCancel}
      data-testid="kyc-review-modal"
    >
      <div
        className="br-card br-aurora-ribbon relative max-w-[600px] w-full px-9 py-10 br-fade-up"
        onClick={(e) => e.stopPropagation()}
        style={
          !isApprove
            ? { borderTop: "3px solid var(--br-red)" }
            : { borderTop: "3px solid var(--br-bronze-soft)" }
        }
      >
        <div
          className="br-overline"
          style={{ color: isApprove ? "var(--br-bronze-soft)" : "var(--br-red)" }}
          data-testid="kyc-review-action"
        >
          {isApprove ? "Approvazione" : "Rifiuto"}
        </div>
        <h2 className="font-serif-display text-[1.9rem] mt-3 leading-[1.1]">
          {row.title_it}
        </h2>
        <div className="mt-2 font-mono-fin text-[0.78rem] text-[color:var(--br-text-dim)]">
          {row.user.name} · {row.user.email} · {row.user.locale.toUpperCase()}
        </div>

        <p className="mt-5 text-[0.92rem] text-[color:var(--br-text)] leading-relaxed">
          {isApprove
            ? "Il cliente riceverà un'email di conferma con la firma del Compliance team. Puoi aggiungere una nota opzionale che apparirà nell'email."
            : "Il cliente riceverà un'email con il motivo del rifiuto e potrà ricaricare o firmare nuovamente il documento. Il commento è obbligatorio."}
        </p>

        <label className="block mt-5">
          <span className="br-overline">
            {isApprove ? "Nota opzionale" : "Motivo del rifiuto"} ·{" "}
            <span style={{ color: "var(--br-text-dim)" }}>locale {row.user.locale.toUpperCase()}</span>
          </span>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={
              isApprove
                ? "Es. Grazie per la rapidità nella sottomissione."
                : "Es. La copia del passaporto è illeggibile, ricaricala a colori..."
            }
            rows={4}
            className="mt-2 w-full bg-transparent border px-3 py-2 text-[0.95rem] text-[color:var(--br-parchment)] outline-none focus:border-[color:var(--br-bronze)] transition-colors resize-none"
            style={{ borderColor: "var(--br-line-bright)" }}
            data-testid="kyc-review-comment"
          />
        </label>

        <div className="mt-7 grid grid-cols-2 gap-3">
          <button
            onClick={onCancel}
            disabled={busy}
            className="br-btn-outline"
            data-testid="kyc-review-cancel"
          >
            Annulla
          </button>
          <button
            onClick={submit}
            disabled={busy}
            className="br-btn-primary"
            style={
              !isApprove
                ? { background: "var(--br-red)", borderColor: "var(--br-red)", color: "white" }
                : undefined
            }
            data-testid="kyc-review-submit"
          >
            {busy
              ? "Invio…"
              : isApprove
              ? "Approva e notifica"
              : "Rifiuta e notifica"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Th({ children, align = "left" }) {
  return (
    <th
      className="px-5 py-4 font-mono-fin text-[0.62rem] tracking-[0.22em] uppercase font-medium"
      style={{ color: "var(--br-text-dim)", textAlign: align }}
    >
      {children}
    </th>
  );
}

function Td({ children, align = "left", className = "" }) {
  return (
    <td className={`px-5 py-4 text-[0.9rem] ${className}`} style={{ textAlign: align }}>
      {children}
    </td>
  );
}
